import React, { useEffect, useRef, useState, useMemo } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { RainbowButton } from '../ui/rainbow-button';

// Theme variables for customization
const theme = {
  message: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    blurAmount: '10px',
    padding: '12px',
    borderRadius: '12px',
    opacity: 1,
  },
  button: {
    marginTop: '24px',
    marginBottom: '30px'
  },
  content: {
    titleMarginBottom: '12px',
    descriptionMarginBottom: '24px',
    sectionPadding: '24px'
  },
  media: {
    verticalPosition: '20%'
  }
};

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  overflow: hidden;
  background: white;
`;

const ImageSection = styled.div`
  position: relative;
  background: #f5f5f5;
  height: ${props => props.$customHeight || '580px'};
  border-radius: 24px;
  overflow: hidden;
  margin: 16px;
  margin-bottom: 0;
  perspective: 1000px;
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const TopBar = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  gap: 8px;
`;

const GlassLabel = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  border-radius: 100px;
  padding: 8px 16px;
  font-size: 14px;
  color: #000;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center ${props => {
    // If the position includes 'px', convert it to percentage
    if (props.$customPosition && props.$customPosition.includes('px')) {
      const pixels = parseInt(props.$customPosition);
      // Convert pixels to percentage (assuming a base height of 500px)
      const percentage = (pixels / 500) * 100;
      return `${percentage}%`;
    }
    return props.$customPosition || props.theme.media.verticalPosition;
  }};
  transition: opacity 0.3s ease-in-out;
  opacity: ${props => props.$isVideoPlaying ? 0 : 1};
`;

const StyledVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center ${props => props.$customPosition || props.theme.media.verticalPosition};
  transition: opacity 0.3s ease-in-out;
  opacity: ${props => props.$isVideoPlaying ? 1 : 0};
`;

const ContentSection = styled.div`
  padding: ${props => props.theme.content.sectionPadding};
  background: white;
  margin: 0 16px 16px;
`;

const Title = styled.div`
  margin-bottom: ${props => props.theme.content.titleMarginBottom};
  
  h1 {
    font-size: 32px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      font-size: 20px;
      gap: 4px;
    }
    
    span {
      font-weight: 400;
      color: #666;
      
      &::before {
        content: '';
        display: inline-block;
        width: 24px;
        height: 1px;
        background-color: #000;
        margin: 0 8px;
        vertical-align: middle;

        @media (max-width: 768px) {
          width: 16px;
          margin: 0 4px;
        }
      }
    }
  }
  
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: #eee;
    margin-top: 16px;
  }
`;

const Description = styled.p`
  color: #666;
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 ${props => props.theme.content.descriptionMarginBottom} 0;
`;

const MessageOverlay = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: auto;
  background: ${props => props.theme.message.backgroundColor};
  backdrop-filter: blur(${props => props.theme.message.blurAmount});
  border-radius: ${props => props.theme.message.borderRadius};
  padding: ${props => props.theme.message.padding};
  opacity: ${props => props.theme.message.opacity};
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 80%;
`;

const MessageIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #007AFF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    display: none;
  }

  /* Hide icon when text is long enough to wrap */
  @media (min-width: 769px) {
    display: ${props => props.textLength > 50 ? 'none' : 'flex'};
  }
`;

const MessageContent = styled.div`
  color: white;
  position: relative;
  padding-left: ${props => props.textLength > 50 ? '12px' : '0'};
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: #007AFF;
    border-radius: 2px;
    opacity: ${props => props.textLength > 50 ? '1' : '0'};
  }
  
  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
  }
  
  p {
    margin: 2px 0 0;
    font-size: 14px;
    opacity: 0.9;
  }
`;

const FlipCard = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s;
  transform-style: preserve-3d;
  transform: ${props => props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0)'};
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const CardFront = styled(CardFace)`
  transform: rotateY(0deg);
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  background: #1A1A1A;
`;

const MentorImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: ${props => `url(${props.$image})`};
  background-size: cover;
  background-position: center;
`;

const MentorInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  background: linear-gradient(to top, rgba(0,0,0,0.9), transparent);
  color: white;

  h3 {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 4px 0;
  }

  p {
    font-size: 16px;
    margin: 0;
    opacity: 0.8;
  }
`;

const ViewMentorButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;

  /* Custom styling for the RainbowButton when used in ViewMentor */
  button {
    height: 36px !important;
    padding: 0 16px !important;
    font-size: 14px !important;
  }
`;

const scrollToContact = (message) => {
  const contactSection = document.getElementById('contact-us');
  const messageInput = document.querySelector('#message');
  
  if (contactSection) {
    contactSection.scrollIntoView({ behavior: 'smooth' });
    
    setTimeout(() => {
      if (messageInput) {
        messageInput.focus();
        messageInput.value = message;
      }
    }, 1000);
  }
};

const WorkerProfile = ({ workerKey, shouldPlayVideo, customHeight, customPosition, hideRainbowButton }) => {
  const { t, tSafe, isLoading, isContentReady } = useLanguage();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [hasAppeared, setHasAppeared] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const mediaRef = useRef(null);

  const workerData = useMemo(() => ({
    name: t(`digitalWorkers.workers.${workerKey}.profile.name`),
    role: t(`digitalWorkers.workers.${workerKey}.profile.role`),
    description: t(`digitalWorkers.workers.${workerKey}.profile.description`),
    cta: t(`digitalWorkers.workers.${workerKey}.profile.cta`),
    greeting: t(`digitalWorkers.workers.${workerKey}.messages.greeting`),
    media: {
      video: t(`digitalWorkers.workers.${workerKey}.profile.media.video`),
      image: t(`digitalWorkers.workers.${workerKey}.profile.media.image`)
    },
    mentor: {
      name: t(`digitalWorkers.workers.${workerKey}.profile.mentor.name`),
      role: t(`digitalWorkers.workers.${workerKey}.profile.mentor.role`),
      image: t(`digitalWorkers.workers.${workerKey}.profile.mentor.image`)
    }
  }), [t, workerKey, isContentReady]);

  // Check if mentor data is valid (not a translation key)
  const hasValidMentor = useMemo(() => {
    return workerData.mentor && 
      workerData.mentor.name && 
      !workerData.mentor.name.startsWith('digitalWorkers') && 
      workerData.mentor.image && 
      !workerData.mentor.image.startsWith('digitalWorkers');
  }, [workerData.mentor]);

  // Debug logging
  useEffect(() => {
    // removed debug log
  }, [workerKey, workerData.mentor, hasValidMentor, isContentReady]);

  // Preload media assets
  useEffect(() => {
    // Don't preload if content isn't ready or if we have translation keys instead of URLs
    if (!isContentReady || 
        workerData.media.image.startsWith('digitalWorkers') || 
        (workerData.media.video && workerData.media.video.startsWith('digitalWorkers'))) {
      return;
    }

    const preloadMedia = async () => {
      try {
        // Preload image
        const img = new Image();
        img.onerror = () => setImageError(true);
        img.src = (workerKey === 'aria') 
          ? 'https://cdn.nexastaff.ai/00-assets/aria-still.jpg?v=20250929'
          : workerData.media.image;

        // Preload video
        if (workerData.media.video) {
          const video = document.createElement('video');
          video.preload = 'auto';
          video.src = workerData.media.video;
          await new Promise((resolve, reject) => {
            video.onloadeddata = resolve;
            video.onerror = () => {
              setVideoError(true);
              reject();
            };
          });
        }
      } catch (error) {
        // silent fail
      }
    };

    preloadMedia();
  }, [workerData.media.video, workerData.media.image, isContentReady, workerKey]);

  // Reset video state when worker changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsVideoPlaying(false);
      setHasAppeared(false);
      setIsHovered(false);
    }
  }, [workerKey]);

  // Handle video playback on first appearance (only if media is in view)
  useEffect(() => {
    if (!shouldPlayVideo || !videoRef.current || videoError || hasAppeared) return;

    const isMediaInView = () => {
      if (!mediaRef.current) return false;
      const rect = mediaRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      // Consider in view if any portion intersects viewport horizontally and vertically
      const verticallyVisible = rect.top < viewportHeight && rect.bottom > 0;
      const horizontallyVisible = rect.left < viewportWidth && rect.right > 0;
      return verticallyVisible && horizontallyVisible;
    };

    if (document.visibilityState === 'visible' && isMediaInView()) {
      videoRef.current.currentTime = 0;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsVideoPlaying(true);
            setHasAppeared(true);
          })
          .catch((error) => {
            setIsVideoPlaying(false);
          });
      }
    }
  }, [shouldPlayVideo, videoError, hasAppeared, workerKey]);

  // Handle video completion
  useEffect(() => {
    if (videoRef.current) {
      const handleVideoEnded = () => {
        if (!isHovered) {
          setIsVideoPlaying(false);
        }
      };

      videoRef.current.addEventListener('ended', handleVideoEnded);
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('ended', handleVideoEnded);
        }
      };
    }
  }, [isHovered]);

  // Handle intersection observer for scroll-based playback (observe only the media area)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          videoRef.current &&
          !hasAppeared &&
          !videoError &&
          document.visibilityState === 'visible'
        ) {
          videoRef.current.currentTime = 0;
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsVideoPlaying(true);
                setHasAppeared(true);
              })
              .catch((error) => {
                setIsVideoPlaying(false);
              });
          }
        }
      },
      { threshold: 0.2, root: null, rootMargin: '0px 0px -10% 0px' }
    );

    if (mediaRef.current) {
      observer.observe(mediaRef.current);
    }

    return () => {
      if (mediaRef.current) {
        observer.unobserve(mediaRef.current);
      }
    };
  }, [hasAppeared, videoError, workerKey]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (videoRef.current && !isVideoPlaying && !videoError) {
      // Reset to beginning if video wasn't playing
      videoRef.current.currentTime = 0;
      
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsVideoPlaying(true);
          })
          .catch((error) => {
            setIsVideoPlaying(false);
          });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsVideoPlaying(false);
    }
  };

  const handleCTAClick = () => {
    const workerName = t(`digitalWorkers.workers.${workerKey}.profile.name`);
    const message = `I would like to know more about ${workerName}...`;
    scrollToContact(message);
  };

  // Helpers to avoid rendering invalid media URLs during initial load
  const isValidUrl = (url) => typeof url === 'string' && /^https?:\/\//.test(url);
  const forcedAriaImage = 'https://cdn.nexastaff.ai/00-assets/aria-still.jpg?v=20250929';
  const computedImageUrl = workerKey === 'aria' ? forcedAriaImage : workerData.media.image;
  const validImageUrl = isValidUrl(computedImageUrl);
  const validVideoUrl = isValidUrl(workerData.media.video);

  return (
    <ThemeProvider theme={theme}>
      <ProfileContainer ref={sectionRef}>
        <ImageSection $customHeight={customHeight}>
          {hasValidMentor && !hideRainbowButton && (
            <ViewMentorButton>
              <RainbowButton onClick={() => setIsFlipped(!isFlipped)}>
                {isFlipped ? 'View Profile' : 'View Mentor'}
              </RainbowButton>
            </ViewMentorButton>
          )}
          
          <FlipCard $isFlipped={isFlipped}>
            <CardFront>
              <TopBar>
                <GlassLabel>{workerData.name}</GlassLabel>
              </TopBar>
              
              <MediaContainer 
                ref={mediaRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {!imageError && validImageUrl && (
                  <StyledImage 
                    $isVideoPlaying={isVideoPlaying}
                    style={{ backgroundImage: `url("${computedImageUrl}")` }}
                    $customPosition={customPosition}
                  />
                )}
                {validVideoUrl && !videoError && (
                  <StyledVideo
                    ref={videoRef}
                    $isVideoPlaying={isVideoPlaying}
                    muted
                    playsInline
                    src={workerData.media.video}
                    poster={computedImageUrl}
                    onError={() => setVideoError(true)}
                    $customPosition={customPosition}
                  />
                )}
              </MediaContainer>
              
              <MessageOverlay>
                <MessageIcon textLength={workerData.greeting.length}>
                  <span role="img" aria-label="email">✉️</span>
                </MessageIcon>
                <MessageContent textLength={workerData.greeting.length}>
                  <h4>{workerData.name}</h4>
                  <p>{workerData.greeting}</p>
                </MessageContent>
              </MessageOverlay>
            </CardFront>

            <CardBack>
              <MentorImage $image={workerData.mentor.image} />
              <MentorInfo>
                <h3>{workerData.mentor.name}</h3>
                <p>{workerData.mentor.role}</p>
              </MentorInfo>
            </CardBack>
          </FlipCard>
        </ImageSection>
        
        <ContentSection>
          <Title>
            <h1>
              {workerData.name}
              <span>{workerData.role}</span>
            </h1>
          </Title>
          <Description>
            {workerData.description}
          </Description>
          {!hideRainbowButton && (
            <div style={{ 
              marginTop: theme.button.marginTop, 
              marginBottom: theme.button.marginBottom
            }}>
              <RainbowButton onClick={handleCTAClick}>
                {t(`digitalWorkers.workers.${workerKey}.profile.cta`)}
              </RainbowButton>
            </div>
          )}
        </ContentSection>
      </ProfileContainer>
    </ThemeProvider>
  );
};

export default WorkerProfile;